<template>
  <tr v-if="!item.deleted">
    <td>
      <el-input
        v-model="item.product_name"
        size="small"
        :disabled="isElementDisabled('product')"
      />
    </td>
    <td>
      <service-pack-type-selector
        :default-pack-type="item.unit_of_measure"
        :disabled="isElementDisabled('pack')"
        @change="onPackTypeChange"
      />
    </td>
    <td></td>
    <td
      v-if="isColumnDisplayed('count')"
    >
      <count
        :count="item.count"
        :item="item"
        :min-count="minCount"
        :disabled="isElementDisabled('count')"
        :compare-items="compareItems"
        :compare-type="compareType"
        @change="onCountChange"
      />
    </td>
    <td
      v-if="isColumnDisplayed('comment')"
    >
      <comment
        :item-comment="item.comment"
        :disabled="isElementDisabled('comment')"
        @change="onCommentChange"
      />
    </td>
    <td
      v-if="isColumnDisplayed('invoiceItemComment')"
    >
      <comment
        :item-comment="item.comment"
        :disabled="isElementDisabled('invoiceItemComment')"
        @change="onCommentChange"
      />
    </td>
    <td
      v-if="isColumnDisplayed('unitPrice')"
    >
      <unit-price
        :unit-price="item.unit_price"
        :currency="currency"
        :disabled="isElementDisabled('unitPrice')"
        @change="onUnitPriceChange"
      />
    </td>
    <td
      v-if="isColumnDisplayed('netPrice')"
    >
      <net-price
        :net-price="item.net_price"
        :currency="currency"
        :disabled="isElementDisabled('netPrice')"
        @change="onNetPriceChange"
      />
    </td>
    <td
      v-if="isColumnDisplayed('vat')"
    >
      <vat-selector
        :vat="item.vat"
        :disabled="isElementDisabled('vat')"
        @change="onVatChange"
      />
    </td>
    <td
      v-if="isColumnDisplayed('actions')"
      style="text-align: right;"
    >
      <actions
        :disabled="isElementDisabled('deleteButton')"
        @delete="onRowDelete"
      />
    </td>
  </tr>
</template>
<script>
import ServicePackTypeSelector from './columns/ServicePackTypeSelector';
import Count from './columns/Count';
import UnitPrice from './columns/UnitPrice';
import NetPrice from './columns/NetPrice';
import VatSelector from './columns/VatSelector';
import Actions from './columns/Actions';
import Comment from './columns/Comment';

export default {
  components: {
    ServicePackTypeSelector,
    Count,
    UnitPrice,
    NetPrice,
    VatSelector,
    Actions,
    Comment,
  },
  props: {
    defaultItem: {
      type: Object,
      default() {
        return {};
      },
      required: true,
    },
    minCount: {
      type: Number,
      default() {
        return 1;
      },
      required: false,
    },
    roundNet: {
      type: Boolean,
      default() {
        return false;
      },
      required: false,
    },
    currency: {
      type: String,
      default() {
        return 'HUF';
      },
      required: false,
    },
    defaultSupplier: {
      type: Object,
      default() {
        return null;
      },
      required: false,
    },
    enabledElements: {
      type: Array,
      default() {
        return ['addButton', 'deleteButton', 'product', 'pack', 'count', 'netPrice', 'vat', 'unitPrice', 'expirationDate', 'expirationDateSelector', 'supplierSelector'];
      },
      required: false,
    },
    enabledColumns: {
      type: Array,
      default() {
        return ['product', 'productPackType', 'expirationDate', 'supplierSelector', 'count', 'unitPrice', 'netPrice', 'vat', 'actions'];
      },
      required: false,
    },
    compareItems: {
      type: Array,
      default() {
        return null;
      },
      required: false,
    },
    compareType: {
      type: String,
      default() {
        return 'diff';
      },
      required: false,
    },
    isVatZero: {
      type: Boolean,
      default() {
        return false;
      },
      required: false,
    },
    isProductSelectFromStock: {
      type: Boolean,
      default() {
        return true;
      },
      required: false,
    },
    availableProducts: {
      validator: prop => typeof prop === 'object' || prop === null,
      default() {
        return null;
      },
      required: false,
    },
    loadBaseCountFromStock: {
      type: Boolean,
      default() {
        return true;
      },
      required: false,
    },
    storageId: {
      type: Number,
      default() {
        return 9;
      }
    },
  },
  data() {
    return {
      baseCountLoading: false,
      item: {
        product_name: '',
        unit_of_measure: 'PIECE',
        count: 1,
        unit_price: 0,
        net_price: 0,
        expiration_date: '',
        base_count: 0,
        comment: '',
      },
    };
  },
  beforeMount() {
    this.item = JSON.parse(JSON.stringify(this.defaultItem));
  },
  async mounted() {
    if (this.item.product && this.item.product.name && this.isProductSelectFromStock) {
      await this.fetchStock();
    }
  },
  methods: {
    onCountChange(count) {
      this.item.count = count;
      this.setNetPrice();
    },
    onUnitPriceChange(unitPrice) {
      this.item.unit_price = unitPrice;
      this.setNetPrice();
    },
    async onPackTypeChange(packType) {
      this.$set(this.item, 'unit_of_measure', packType);
    },
    onNetPriceChange(price) {
      if (this.roundNet) {
        this.item.net_price = Math.round(price);
      } else {
        this.item.net_price = price;
      }
      this.item.unit_price = parseFloat(this.item.net_price) / parseInt(this.item.count);
      this.priceChange();
    },
    onVatChange(vat) {
      this.item.vat = vat;
      this.priceChange();
    },
    onCommentChange(comment) {
      this.$set(this.item, 'comment', comment);
      this.priceChange();
    },
    onRowDelete() {
      this.$set(this.item, 'deleted', true);
      this.priceChange();
    },
    setNetPrice() {
      if (this.roundNet) {
        this.item.net_price = Math.round(this.item.unit_price * this.item.count);
      } else {
        this.item.net_price = this.item.unit_price * this.item.count;
      }
      this.priceChange();
    },
    priceChange() {
      this.$emit('priceChange');
    },
    isElementDisabled(elementName) {
      return !this.enabledElements.includes(elementName);
    },
    isColumnDisplayed(column) {
      return this.enabledColumns.includes(column);
    },
  },
};
</script>
